import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { Lead, LeadNote, Company, WhatsappInstance } from '../../types';
import { Send, Image as ImageIcon, FileText, Loader2, Edit3, Clock, Download, Smile, User, Video, Paperclip, CheckCircle2, Bot, Calendar, Mic, Square, Trash2, X, Zap, MessageCircle, Minus, Tag } from 'lucide-react';
import { fetchWithAuth, getEdgeFunctionErrorMessage } from '../../lib/api';
import { toast } from 'sonner';
import { instanceLabel, instancesAvailableTo, isRestrictedToOwnInstance } from '../../lib/whatsappInstances';
import {
    markLeadMessagesAsRead, fetchUserName, subscribeToLeadChat, fetchAvailableInstances,
    fetchChatHistory, addLeadNote, uploadWhatsappMedia, sendWhatsappMessage, saveOutboundMessage,
    syncWhatsappHistory, fetchLeadMessages, ensureWhatsappWebhooks,
} from '../../services/whatsappService';
import { EmojiPicker } from '../whatsapp/EmojiPicker';
import { QuickRepliesPicker } from './QuickRepliesPicker';
import { MessageBubble } from './chat/MessageBubble';
import { mergeMessages, ChatMessage } from './chat/mergeMessages';
import { AiSuggestionBar } from './chat/AiSuggestionBar';
import { reviewSuggestion, canUseAiAgent } from '../../services/aiAgentService';

const formatDateSeparator = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    const isYesterday = date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear();

    if (isToday) return 'Hoje';
    if (isYesterday) return 'Ontem';

    return date.toLocaleDateString('pt-BR', { 
        day: 'numeric', 
        month: 'long', 
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined 
    });
};

interface LeadWhatsappChatProps {
    isFullHeight?: boolean;
    lead: Lead;
    company: Company;
    onMessageSent?: () => void;
    onMarkAsRead?: () => void;
    onClose?: () => void;
}

export function LeadWhatsappChat({ lead, company, onMessageSent, onMarkAsRead, onClose, isFullHeight }: LeadWhatsappChatProps) {
    const { user } = useAuth();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [notes, setNotes] = useState<(LeadNote & { users?: { name: string } })[]>([]);

    // Só as últimas PAGE_SIZE entradas vão para o DOM; as anteriores entram sob demanda.
    // (Centenas de balões com imagem/áudio/vídeo deixavam o chat lento e travando.)
    const PAGE_SIZE = 120;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const prevScrollHeightRef = useRef<number | null>(null);

    // Mantém o mesmo objeto de mensagem em `entry.msg` para o React.memo do balão funcionar.
    const combined = React.useMemo(() => {
        return [
            ...messages.map(m => ({ type: 'msg' as const, id: m.id, created_at: m.created_at, time: new Date(m.created_at).getTime(), msg: m })),
            ...notes.map(n => ({ type: 'note' as const, id: n.id, created_at: n.created_at, time: new Date(n.created_at).getTime(), note: n }))
        ].sort((a, b) => a.time - b.time);
    }, [messages, notes]);

    const visibleItems = React.useMemo(() => combined.slice(-visibleCount), [combined, visibleCount]);
    const hiddenCount = combined.length - visibleItems.length;

    const [isNoteMode, setIsNoteMode] = useState(false);
    // Prefixo "*Nome*:" com o nome de quem está atendendo. Fica ligado por padrão
    // (comportamento de sempre); o atendente pode desligar por mensagem.
    const [signMessage, setSignMessage] = useState(true);
    const [showQuickReplies, setShowQuickReplies] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement>(null);
    const [newMessage, setNewMessage] = useState('');
    // Sugestão do atendente de IA que o vendedor escolheu usar (para registrar se enviou como estava ou editada).
    const aiSuggestionRef = useRef<{ id: string; text: string } | null>(null);
    const lastOutboundAt = React.useMemo(() => {
        let last: number | null = null;
        for (const m of messages) {
            if (m.direction !== 'OUTBOUND') continue;
            const t = new Date(m.created_at).getTime();
            if (last === null || t > last) last = t;
        }
        return last;
    }, [messages]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Instances state
    const [instances, setInstances] = useState<WhatsappInstance[]>([]);
    const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');

    // Media states
    const [attachment, setAttachment] = useState<File | null>(null);
    const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
    
    // Recording states
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        fetchMessagesAndNotes();

        const markAsRead = async () => {
            if (!company) return;
            try {
                await markLeadMessagesAsRead(lead.id);
                onMarkAsRead?.();
            } catch (e) {
                console.error('Error marking messages as read', e);
            }
        };
        markAsRead();

        if (!company) return;

        const unsubscribe = subscribeToLeadChat(lead.id, {
            onMessageChange: (message) => {
                // Mark as read immediately if it's a new inbound message and we are looking at the chat
                if (message.direction === 'INBOUND' && message.status === 'SENT') {
                    markAsRead();
                }

                setMessages(prev => {
                    // Existe por id interno ou pelo message_id do WhatsApp?
                    const existingIndex = prev.findIndex(m =>
                        m.id === message.id ||
                        (m.message_id && message.message_id && m.message_id === message.message_id)
                    );

                    if (existingIndex >= 0) {
                        const newArr = [...prev];
                        newArr[existingIndex] = { ...newArr[existingIndex], ...message, isPending: false };
                        return newArr;
                    }
                    return [...prev, message];
                });
                scrollToBottom();
            },
            onNoteInserted: async (newNote) => {
                const userName = await fetchUserName(newNote.user_id);
                const noteWithUser = { ...newNote, users: { name: userName } };
                setNotes(prev => {
                    if (prev.find(n => n.id === newNote.id)) return prev;
                    return [...prev, noteWithUser];
                });
                scrollToBottom();
            }
        });

        return () => {
            unsubscribe();
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [lead.id, company]);

    // Polling de segurança (o realtime é o caminho principal): 15s e só com a aba visível.
    useEffect(() => {
        const intervalId = setInterval(() => {
            if (document.hidden) return;
            fetchMessagesAndNotes();
        }, 15000);
        const onVisible = () => { if (!document.hidden) fetchMessagesAndNotes(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [lead.id, company]);

    // Garante, uma vez por sessão do navegador, que as instâncias da empresa estejam assinadas
    // nos eventos de recibo (entregue/lida) do WhatsApp.
    useEffect(() => {
        if (!company) return;
        const key = `wa-webhooks-ok-${company.id}`;
        try { if (sessionStorage.getItem(key)) return; } catch { /* sessionStorage indisponível */ }
        ensureWhatsappWebhooks(company.id)
            .then(() => { try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ } })
            .catch(e => console.warn('Não foi possível atualizar a assinatura de recibos do WhatsApp', e));
    }, [company]);

    // Fetch instances and pre-select
    useEffect(() => {
        const fetchInstances = async () => {
            if (!company) return;
            try {
                const data = await fetchAvailableInstances(company.id);
                const available = instancesAvailableTo(data, user?.role, user?.id);
                setInstances(available);

                if (available.length > 0) {
                    // Vendedor começa pela própria instância; a padrão da empresa fica como alternativa
                    const myInstance = isRestrictedToOwnInstance(user?.role) ? available.find(i => i.user_id === user?.id) : undefined;
                    const ownerInstance = available.find(i => i.user_id === lead.user_id);
                    const defaultInstance = available.find(i => i.is_default);

                    if (myInstance) setSelectedInstanceId(myInstance.id);
                    else if (ownerInstance) setSelectedInstanceId(ownerInstance.id);
                    else if (defaultInstance) setSelectedInstanceId(defaultInstance.id);
                    else setSelectedInstanceId(available[0].id);
                } else {
                    setSelectedInstanceId('');
                }
            } catch (error) {
                console.error('Error fetching instances:', error);
            }
        };
        fetchInstances();
    }, [company, lead.user_id, user?.id, user?.role]);

    // Ao abrir mensagens anteriores, mantém o ponto de leitura (senão a tela pula para o topo).
    React.useLayoutEffect(() => {
        const container = chatContainerRef.current;
        if (prevScrollHeightRef.current != null && container) {
            container.scrollTop += container.scrollHeight - prevScrollHeightRef.current;
            prevScrollHeightRef.current = null;
        }
    }, [visibleCount]);

    const fetchMessagesAndNotes = async () => {
        if (!company) return;
        try {
            const { messages: dbMessages, notes: notesWithUsers } = await fetchChatHistory(lead.id, lead.phone, company.id);

            setMessages(prev => {
                const next = mergeMessages(prev, dbMessages as ChatMessage[]);
                if (next.length > prev.length) scrollToBottom();
                return next;
            });
            setNotes(prev => {
                const dbNotes = notesWithUsers;
                const pendingNotes = prev.filter(n => (n as any).isPending);
                const finalNotes = [...dbNotes, ...pendingNotes];
                finalNotes.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                
                if (prev.length !== finalNotes.length) scrollToBottom(true);
                return finalNotes as any;
            });
        } catch (error) {
            console.error('Error fetching chat history:', error);
            toast.error('Erro ao carregar histórico do chat');
        } finally {
            setLoading(false);
        }
    };

    const handleSyncHistory = async (isAutomatic = false) => {
        if (!company || !user) return;
        if (isAutomatic) return;

        setSyncing(true);
        try {
            if (!isAutomatic) toast.info('Sincronizando via WuzAPI...');

            const response = await syncWhatsappHistory({ leadId: lead.id, companyId: company.id, phone: lead.phone });

            if (response.error) {
                console.error('Edge function error object:', response.error);
                const errorMessage = await getEdgeFunctionErrorMessage(
                    response.error,
                    'Erro ao solicitar histórico. Verifique as credenciais da WuzAPI.'
                );

                if (!isAutomatic) toast.error(`Erro: ${errorMessage}`);
                throw new Error(errorMessage);
            }

            setMessages(await fetchLeadMessages(lead.id));
            if (!isAutomatic) toast.success('Histórico sincronizado com sucesso!');
        } catch (error: any) {
            console.error('Error syncing history via WuzAPI:', error);
            // O erro já foi mostrado no toast no bloco acima se veio da edge function
        } finally {
            setSyncing(false);
        }
    };

    const scrollToBottom = (force = false) => {
        if (!force && chatContainerRef.current) {
            const container = chatContainerRef.current;
            const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 300;
            if (!isNearBottom) return;
        }
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setAttachment(file);
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => setAttachmentPreview(e.target?.result as string);
            reader.readAsDataURL(file);
        } else {
            setAttachmentPreview(null);
        }
    };

    const clearAttachment = () => {
        setAttachment(null);
        setAttachmentPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const file = new File([audioBlob], `audio_${Date.now()}.webm`, { type: 'audio/webm' });
                setAttachment(file);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (error) {
            console.error('Error accessing microphone:', error);
            toast.error('Erro ao acessar o microfone. Verifique as permissões.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerRef.current) clearInterval(timerRef.current);
        }
    };

    const cancelRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            audioChunksRef.current = [];
            setIsRecording(false);
            if (timerRef.current) clearInterval(timerRef.current);
            // Delete blob right after creation in onstop
            setTimeout(() => clearAttachment(), 50);
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (isRecording) {
            toast.warning('Pare a gravação antes de enviar.');
            return;
        }

        if (!newMessage.trim() && !attachment) return;
        if (!company || !user) return;

        if (!isNoteMode && (!lead?.phone || !lead.phone.replace(/\D/g, ''))) {
            toast.error('Este lead não possui um número de telefone válido para envio pelo WhatsApp.');
            return;
        }

        if (!isNoteMode && !selectedInstanceId && isRestrictedToOwnInstance(user.role)) {
            toast.error('Você não tem um WhatsApp conectado. Peça ao gestor para vincular uma instância a você em Configurações > Integrações.');
            return;
        }

        setSending(true);
        const currentMessage = newMessage.trim();
        const currentAttachment = attachment;
        const aiSug = isNoteMode ? null : aiSuggestionRef.current;
        
        setNewMessage('');
        clearAttachment();
        
        const tempId = `temp-${Date.now()}`;
        const prefixedMessage = currentMessage
            ? (signMessage ? `*${user.name}*:\n${currentMessage}` : currentMessage)
            : '';
        
        // Optimistic UI update
        if (isNoteMode) {
            setNotes(prev => [...prev, {
                id: tempId as any,
                lead_id: lead.id,
                user_id: user.id,
                content: currentMessage,
                created_at: new Date().toISOString(),
                users: { name: user.name }
            } as any]);
        } else {
            let msgType = 'text';
            if (currentAttachment) {
                if (currentAttachment.type.startsWith('image/')) msgType = 'image';
                else if (currentAttachment.type.startsWith('audio/')) msgType = 'audio';
                else if (currentAttachment.type.startsWith('video/')) msgType = 'video';
                else msgType = 'document';
            }
            
            setMessages(prev => [...prev, {
                id: tempId,
                company_id: company.id,
                lead_id: lead.id,
                message_body: prefixedMessage || `[${msgType === 'audio' ? 'Áudio' : msgType === 'image' ? 'Imagem' : 'Documento'}]`,
                direction: 'OUTBOUND',
                message_type: msgType,
                instance_id: selectedInstanceId,
                created_at: new Date().toISOString(),
                isPending: true
            } as any]);
        }
        
        scrollToBottom(true);
        if (onMessageSent && !isNoteMode) {
            onMessageSent();
        }

        try {
            if (isNoteMode) {
                const newNote = await addLeadNote(lead.id, user.id, currentMessage);
                setNotes(prev => prev.map(n => n.id === tempId as any ? { ...newNote, users: { name: user.name } } : n));
            } else {
                let mediaPayload = null;
                let mediaUrl: string | null = null;
                let msgType = 'text';

                if (currentAttachment) {
                    const base64Full = await fileToBase64(currentAttachment);
                    mediaPayload = {
                        data: base64Full,
                        type: currentAttachment.type,
                        name: currentAttachment.name
                    };

                    if (currentAttachment.type.startsWith('image/')) msgType = 'image';
                    else if (currentAttachment.type.startsWith('audio/')) msgType = 'audio';
                    else if (currentAttachment.type.startsWith('video/')) msgType = 'video';
                    else msgType = 'document';

                    mediaUrl = await uploadWhatsappMedia(lead.id, currentAttachment);
                }

                // Send via WuzAPI through Edge Function
                const { error: sendError, messageId: wuzapiMessageId } = await sendWhatsappMessage({
                    leadId: lead.id,
                    companyId: company.id,
                    phone: lead.phone,
                    message: currentMessage,
                    media: mediaPayload,
                    instanceId: selectedInstanceId,
                    signMessage
                });

                if (sendError) {
                    let detailedError = await getEdgeFunctionErrorMessage(
                        sendError,
                        'Falha ao enviar mensagem pelo WhatsApp.'
                    );
                    if (detailedError && detailedError.toLowerCase().includes('lid found')) {
                        detailedError = 'O número de telefone aparenta ser inválido ou não pode receber mensagens no momento. Por favor, verifique o cadastro do Lead.';
                    }
                    throw new Error(detailedError);
                }

                if (aiSug) {
                    aiSuggestionRef.current = null;
                    reviewSuggestion(aiSug.id, currentMessage === aiSug.text ? 'sent' : 'edited', currentMessage, user.id)
                        .catch(err => console.error('Erro ao registrar a revisão da sugestão da IA:', err));
                }

                // Save to DB. A mensagem já saiu pelo WhatsApp: se o registro falhar, não removemos a
                // bolha nem pedimos para reenviar (duplicaria para o cliente); a próxima atualização
                // do chat traz a mensagem que o webhook gravou.
                const corpo = prefixedMessage || `[${msgType === 'audio' ? 'Áudio' : msgType === 'image' ? 'Imagem' : 'Documento'}]`;
                try {
                    const newMsg = await saveOutboundMessage({
                        companyId: company.id,
                        leadId: lead.id,
                        userId: user.id,
                        messageBody: corpo,
                        messageType: msgType,
                        mediaUrl,
                        instanceId: selectedInstanceId,
                        whatsappMessageId: wuzapiMessageId
                    });
                    // O realtime pode já ter trazido esta mesma mensagem (mesmo id): evita bolha duplicada.
                    setMessages(prev => {
                        const seen = new Set<string>();
                        return prev
                            .map(m => m.id === tempId ? { ...newMsg, isPending: false } : m)
                            .filter(m => !seen.has(m.id) && !!seen.add(m.id));
                    });
                } catch (saveError) {
                    console.error('Mensagem enviada, mas não foi registrada no histórico:', saveError);
                    setMessages(prev => prev.map(m => m.id === tempId ? { ...m, isPending: false } : m));
                    toast.warning('Mensagem enviada ao WhatsApp, mas houve um erro ao registrar no histórico.');
                }
            }
        } catch (error: any) {
            console.error('Error sending message:', error);
            let userFriendlyError = error?.message || String(error) || 'Erro desconhecido';
            if (typeof userFriendlyError === 'string' && userFriendlyError.toLowerCase().includes('lid found')) {
                userFriendlyError = 'O número de telefone aparenta ser inválido ou não pode receber mensagens no momento. Por favor, verifique o cadastro do Lead.';
            }
            toast.error(`Erro ao enviar: ` + userFriendlyError);
            if (isNoteMode) {
                setNotes(prev => prev.filter(n => n.id !== tempId as any));
            } else {
                setMessages(prev => prev.filter(m => m.id !== tempId));
            }
            // Restore inputs on error
            setNewMessage(currentMessage);
            if (currentAttachment) setAttachment(currentAttachment);
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-green-700" />
            </div>
        );
    }

    return (
        <div className={`flex flex-col max-h-full bg-white dark:bg-slate-900 overflow-hidden relative ${isFullHeight ? 'h-full' : 'h-[600px] rounded-2xl border border-gray-200/50 dark:border-gray-800 shadow-xl'}`}>
            
            {/* Header Glassmorphism */}
            <div className="absolute top-0 left-0 right-0 z-30 px-5 py-4 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center text-green-700 dark:text-green-400 font-bold shadow-sm">
                        {lead.name ? lead.name.charAt(0).toUpperCase() : <User size={20} />}
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white leading-tight">{lead.name || 'Sem Nome'}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">{lead.phone || 'Sem número'}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleSyncHistory(false)}
                        disabled={syncing}
                        className="text-xs font-medium flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
                    >
                        {syncing ? <Loader2 size={14} className="animate-spin" /> : <Clock size={14} />}
                        {syncing ? 'Sincronizando...' : 'Puxar Histórico'}
                    </button>
                    {onClose && (
                        <button aria-label="Fechar"
                            onClick={onClose}
                            className="p-1.5 text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                        >
                            <X size={20} />
                        </button>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto px-4 pt-24 pb-4 space-y-4" style={{ backgroundColor: '#f0f2f5', backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")', backgroundSize: '400px', backgroundRepeat: 'repeat', backgroundBlendMode: 'overlay' }}>
                <div className="dark:absolute dark:inset-0 dark:bg-slate-900/90 dark:z-0 pointer-events-none" />
                
                <div className="relative z-10 space-y-4">
                    {combined.length === 0 ? (
                        <div className="text-center py-8">
                            <span className="bg-white/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 px-4 py-2 rounded-full text-sm inline-block shadow-sm backdrop-blur-sm border border-slate-200 dark:border-slate-700">
                                Nenhuma mensagem enviada ainda. Inicie a conversa!
                            </span>
                        </div>
                    ) : (
                        <>
                            {hiddenCount > 0 && (
                                <div className="flex justify-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            prevScrollHeightRef.current = chatContainerRef.current?.scrollHeight ?? null;
                                            setVisibleCount(c => c + PAGE_SIZE);
                                        }}
                                        className="bg-white/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-300 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 transition-colors"
                                    >
                                        Carregar mensagens anteriores ({hiddenCount})
                                    </button>
                                </div>
                            )}
                            {visibleItems.map((item, index) => {
                                const itemDate = new Date(item.time).toLocaleDateString('pt-BR');
                                const prevItemDate = index > 0 ? new Date(visibleItems[index - 1].time).toLocaleDateString('pt-BR') : null;
                                const showDateSeparator = itemDate !== prevItemDate;

                                return (
                                    <React.Fragment key={`${item.type}-${item.id}`}>
                                        {showDateSeparator && (
                                            <div className="flex justify-center my-4">
                                                <span className="bg-white/80 dark:bg-slate-800/80 text-slate-500 dark:text-slate-400 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm border border-slate-100 dark:border-slate-700/50 backdrop-blur-sm">
                                                    {formatDateSeparator(item.created_at)}
                                                </span>
                                            </div>
                                        )}
                                        {item.type === 'note' ? (
                                            <div className="flex justify-center my-6">
                                                <div className="max-w-[85%] bg-yellow-50/90 dark:bg-yellow-900/30 backdrop-blur-sm border border-yellow-200 dark:border-yellow-700/50 rounded-2xl px-5 py-3 shadow-sm relative">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Edit3 size={14} className="text-yellow-700 dark:text-yellow-500" />
                                                        <span className="text-xs font-bold text-yellow-700 dark:text-yellow-500 uppercase tracking-wider">
                                                            Nota Interna • {item.note.users?.name || 'Usuário'}
                                                        </span>
                                                    </div>
                                                    <p className="whitespace-pre-wrap text-sm text-yellow-900 dark:text-yellow-100">{item.note.content}</p>
                                                </div>
                                            </div>
                                        ) : (
                                            <MessageBubble message={item.msg} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input Area Glassmorphism */}
            <div className="relative z-30 px-4 py-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-t border-gray-100 dark:border-gray-800">
                
                {!isNoteMode && instances.length === 0 && isRestrictedToOwnInstance(user?.role) && (
                    <div className="mb-3 px-3 py-2 text-xs rounded-xl border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300">
                        Você não tem um WhatsApp conectado. Peça ao gestor para vincular uma instância a você.
                    </div>
                )}

                {!isNoteMode && instances.length > 0 && (
                    <div className="mb-3 px-1 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30 p-2 rounded-xl border border-slate-100/50 dark:border-slate-700/30">
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 font-medium tracking-wide uppercase">
                            <MessageCircle size={14} className="text-green-500" />
                            <span>Canal de Envio</span>
                        </div>
                        <div className="relative group">
                            <select
                                value={selectedInstanceId}
                                onChange={(e) => setSelectedInstanceId(e.target.value)}
                                className="appearance-none cursor-pointer pl-3 pr-7 py-1.5 text-xs font-semibold bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-700 dark:text-slate-200 hover:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-500/20 shadow-sm transition-all"
                            >
                                {instances.map(inst => (
                                    <option key={inst.id} value={inst.id} className="dark:bg-slate-800">
                                        {instanceLabel(inst)} {inst.is_default ? '(Padrão)' : ''}
                                    </option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500 group-hover:text-green-500 transition-colors">
                                <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
                            </div>
                        </div>
                    </div>
                )}

                {!isNoteMode && user && canUseAiAgent(user.role, user.company_id, user.id) && (
                    <AiSuggestionBar
                        leadId={lead.id}
                        userId={user.id}
                        userRole={user.role}
                        lastOutboundAt={lastOutboundAt}
                        onUse={(text, id) => { setNewMessage(text); aiSuggestionRef.current = { id, text }; }}
                    />
                )}

                {/* Attachment Preview */}
                {attachment && !isRecording && (
                    <div className="absolute bottom-full left-4 mb-3 p-2 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 flex items-center gap-3 animate-in slide-in-from-bottom-2">
                        {attachmentPreview ? (
                            <img src={attachmentPreview} className="w-12 h-12 object-cover rounded-lg border border-gray-100 dark:border-gray-700" />
                        ) : (
                            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
                                {attachment.type.startsWith('audio/') ? <Mic className="text-slate-500" /> : <FileText className="text-slate-500" />}
                            </div>
                        )}
                        <div className="flex flex-col pr-4">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate w-32">{attachment.name}</span>
                            <span className="text-[11px] text-slate-500">{(attachment.size / 1024).toFixed(1)} KB</span>
                        </div>
                        <button aria-label="Fechar" type="button" onClick={clearAttachment} className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors absolute -top-2 -right-2 bg-white dark:bg-slate-800 shadow-sm border border-gray-100 dark:border-gray-700">
                            <X size={14} />
                        </button>
                    </div>
                )}

                <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                    {/* Note Mode Toggle */}
                    <button 
                        type="button" 
                        onClick={() => setIsNoteMode(!isNoteMode)}
                        className={`p-2.5 rounded-full transition-all ${isNoteMode ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400 shadow-inner' : 'text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`} 
                        title={isNoteMode ? "Sair da Nota Interna" : "Criar Nota Interna"}
                    >
                        <Edit3 size={20} />
                    </button>

                    {!isNoteMode && (
                        <>
                            {/* Assinatura do atendente na mensagem ("*Nome*:" antes do texto) — opcional por mensagem */}
                            <button
                                type="button"
                                onClick={() => setSignMessage(!signMessage)}
                                className={`p-2.5 rounded-full transition-all ${signMessage ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 shadow-inner' : 'text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                                title={signMessage ? `Assinando como "${user?.name}" — clique para enviar sem assinatura` : 'Enviar sem assinatura — clique para assinar com seu nome'}
                            >
                                <Tag size={20} />
                            </button>
                            <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*,video/*,audio/*,.pdf,.doc,.docx" />
                            {/* Quick Replies */}
                            <div className="relative">
                                <button type="button" onClick={() => { setShowQuickReplies(!showQuickReplies); setShowEmojiPicker(false); }} className="p-2.5 text-slate-500 hover:text-yellow-500 transition-colors" title="Respostas Rápidas">
                                    <Zap size={20} />
                                </button>
                                {showQuickReplies && (
                                    <QuickRepliesPicker
                                        companyId={company?.id ?? lead.company_id}
                                        companyName={company?.name}
                                        leadName={lead.name}
                                        leadStatus={lead.status}
                                        niche={company?.niche}
                                        onPick={(text) => { setNewMessage(text); setShowQuickReplies(false); }}
                                    />
                                )}
                            </div>
                            {/* Emoji Picker */}
                            <div className="relative" ref={emojiPickerRef}>
                                <button
                                    type="button"
                                    onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowQuickReplies(false); }}
                                    className={`p-2.5 transition-colors ${showEmojiPicker ? 'text-yellow-500' : 'text-slate-500 hover:text-yellow-500'}`}
                                    title="Emojis"
                                >
                                    <Smile size={20} />
                                </button>
                                {showEmojiPicker && (
                                    <EmojiPicker
                                        onSelect={(emoji) => {
                                            setNewMessage(prev => prev + emoji);
                                        }}
                                        onClose={() => setShowEmojiPicker(false)}
                                    />
                                )}
                            </div>
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="p-2.5 text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors" title="Anexar Arquivo">
                                <Paperclip size={20} />
                            </button>
                        </>
                    )}

                    {/* Main Input Area */}
                    <div className={`flex-1 rounded-2xl flex items-center min-h-[44px] transition-all overflow-hidden ${isNoteMode ? 'bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-200 dark:border-yellow-700/50' : 'bg-slate-100 dark:bg-slate-800 border-2 border-transparent focus-within:border-green-500/30 dark:focus-within:border-green-500/30'}`}>
                        {isRecording ? (
                            <div className="flex-1 flex items-center justify-between px-4 py-2 w-full">
                                <div className="flex items-center gap-3">
                                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                                    <span className="text-red-500 font-medium font-mono">
                                        {Math.floor(recordingTime / 60).toString().padStart(2, '0')}:
                                        {(recordingTime % 60).toString().padStart(2, '0')}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button type="button" onClick={cancelRecording} className="p-2 text-slate-500 hover:text-red-500 transition-colors" title="Cancelar">
                                        <Trash2 size={18} />
                                    </button>
                                    <button type="button" onClick={stopRecording} className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-sm ml-1" title="Parar e Anexar">
                                        <Square size={14} fill="currentColor" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <textarea
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        if (newMessage.trim() || attachment) handleSendMessage(e as any);
                                    }
                                }}
                                placeholder={isNoteMode ? "Escreva uma nota interna (visível só para equipe)..." : "Mensagem..."}
                                className={`w-full bg-transparent border-none focus:ring-0 resize-none py-3 px-4 max-h-32 text-[15px] outline-none ${isNoteMode ? 'text-yellow-900 dark:text-yellow-100 placeholder-yellow-600/50 dark:placeholder-yellow-500/50' : 'text-slate-900 dark:text-slate-100 placeholder-slate-400'}`}
                                rows={1}
                                style={{ minHeight: '48px' }}
                            />
                        )}
                    </div>
                    
                    {/* Send / Mic Button */}
                    {!isNoteMode && !newMessage.trim() && !attachment && !isRecording ? (
                        <button 
                            type="button" 
                            onClick={startRecording}
                            className="p-3 bg-green-700 hover:bg-green-800 text-white rounded-full transition-all shadow-sm hover:shadow active:scale-95"
                            title="Gravar Áudio"
                        >
                            <Mic size={20} />
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={sending || isRecording || (!newMessage.trim() && !attachment)}
                            className={`p-3 rounded-full transition-all shadow-sm active:scale-95 ${
                                isNoteMode 
                                    ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
                                    : 'bg-green-700 hover:bg-green-800 text-white'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} className="ml-0.5" />}
                        </button>
                    )}
                </form>
            </div>
        </div>
    );
}
